/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { usePathname } from 'next/navigation';
import { FooterLinkSection } from './FooterLinkSection';
import { IFooterLinkSections } from './types';
import { Color } from '../../../styles/Color';
import { FooterFinePrint } from './FooterFinePrint';
import FooterSocialsSection from './FooterSocialsSection';
import {
	ContentWrap,
	Css,
	ITheme,
	IUrlLinkItem,
	Responsive,
} from '../../../ui';
import { FooterAppInfo } from './FooterAppInfo';
import { ISocialLinksProps } from '../social-links/SocialLinks';
import { PageRoutes } from '../../../features/app/pages/domain/constants/PageRoutes';

export interface IFooterProps {
	linkSections: IFooterLinkSections;
	socialLinks?: ISocialLinksProps;
	appStoreLinks?: ISocialLinksProps;
	finePrint: IUrlLinkItem[];
	children?: ReactNode;
}

export function Footer({
	linkSections,
	socialLinks,
	finePrint,
	appStoreLinks,
}: IFooterProps) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const displayAppDetails =
		appStoreLinks?.links && appStoreLinks.links.length > 0;
	const pathname = usePathname();
	const isGateway = pathname === PageRoutes.Gateway;

	return (
		<StyledFooter>
			<StyledTopSection isGateway={isGateway}>
				<StyledContentWrap>
					<FooterGrid alignLeft={!displayAppDetails}>
						{linkSections.map(({ key, title, ...props }) => (
							<FooterLinkSection title={title} key={key} {...props} />
						))}
						{displayAppDetails && <FooterAppInfo links={appStoreLinks.links} />}
					</FooterGrid>
				</StyledContentWrap>
			</StyledTopSection>

			<StyledBottomSection>
				{!isMobileXL && socialLinks && (
					<FooterSocialsSection socialLinks={socialLinks} />
				)}
				<FooterFinePrint socialLinks={socialLinks} links={finePrint} />
			</StyledBottomSection>
		</StyledFooter>
	);
}

const StyledContentWrap = styled(ContentWrap)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		margin-left: ${Css.pxToRem(24)};
		margin-right: ${Css.pxToRem(24)};
	}
`;

const footerGridStyles = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(${Css.pxToRem(200)}, 1fr));
	justify-content: start;
	gap: ${Css.pxToRem(16)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		grid-template-columns: repeat(2, 2fr);
	}
`;

const footerFlexStyles = css`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	row-gap: ${Css.pxToRem(40)};
	column-gap: ${Css.pxToRem(16)};
	align-items: flex-start;
`;

const FooterGrid = styled.div<{ alignLeft: boolean }>`
	${({ alignLeft }) => (alignLeft ? footerGridStyles : footerFlexStyles)}
`;

export const StyledFooter = styled.footer<{}, ITheme>`
	border-top: 1px solid ${Color.Neutral94};
	position: relative;
	bottom: 0;
`;

const StyledTopSection = styled.div<{ isGateway: boolean }, ITheme>`
	background-color: ${(props) =>
		props.isGateway ? Color.Neutral2 : props.theme.color.primaryLight};
	padding-bottom: ${Css.pxToRem(40)};
	padding-top: ${Css.pxToRem(40)};
`;

const StyledBottomSection = styled.div<{}, ITheme>`
	color: ${(props) => props.theme.color.onSecondary};
	background-color: ${(props) => props.theme.color.secondary};
`;
